import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Select, Tag, Typography, message, Tooltip } from "antd";
import {
  SaveOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  RobotOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { saveCourseLevel } from "../../../appRedux/actions/courses";
import AIGenerateAction from "../../Common/AIGenerateAction";
import moment from "moment";

const { Text } = Typography;

const VALID_LABELS = ["beginner", "intermediate", "advanced"];

const LEVEL_COLORS = {
  beginner: { tag: "green", bg: "#f6ffed", border: "#b7eb8f", text: "#389e0d", bar: "#52c41a" },
  intermediate: { tag: "blue", bg: "#e6f7ff", border: "#91d5ff", text: "#096dd9", bar: "#1890ff" },
  advanced: { tag: "purple", bg: "#f9f0ff", border: "#d3adf7", text: "#531dab", bar: "#722ed1" },
};

const DEPTH_COLORS = {
  basic: "#52c41a",
  moderate: "#faad14",
  deep: "#f5222d",
};

// ── Normalize AI response to structured level object ──
const normalizeLevelFromAI = (payload) => {
  if (!payload) return null;

  // Handle plain string
  if (typeof payload === "string") {
    const label = payload.trim().toLowerCase();
    if (VALID_LABELS.includes(label)) {
      return { label, confidence: null, scores: null, reasoning: null, signals: null };
    }
    return null;
  }

  if (typeof payload === "object") {
    const label = (
      payload.label || payload.level || payload.difficulty || payload.classification || ""
    ).trim().toLowerCase();

    if (!VALID_LABELS.includes(label)) return null;

    let scores = payload.scores || null;
    if (scores && typeof scores === "object") {
      scores = {
        beginner: typeof scores.beginner === "number" ? scores.beginner : 0,
        intermediate: typeof scores.intermediate === "number" ? scores.intermediate : 0,
        advanced: typeof scores.advanced === "number" ? scores.advanced : 0,
      };
    }

    return {
      label,
      confidence:
        typeof payload.confidence === "number"
          ? Math.min(1, Math.max(0, payload.confidence))
          : null,
      scores,
      reasoning: payload.reasoning || payload.rationale || payload.explanation || null,
      signals: payload.signals || null,
    };
  }

  return null;
};

// ── Scores horizontal bar chart ──
const ScoresBar = ({ scores, compact = false }) => {
  if (!scores) return <Text type="secondary" style={{ fontSize: 12 }}>No scores available</Text>;

  const maxScore = Math.max(scores.beginner || 0, scores.intermediate || 0, scores.advanced || 0, 0.01);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 4 : 6 }}>
      {VALID_LABELS.map((level) => {
        const score = scores[level] || 0;
        const pct = (score / maxScore) * 100;
        const colors = LEVEL_COLORS[level];
        return (
          <div key={level} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ width: 85, fontSize: 12, textTransform: "capitalize", color: "#595959" }}>
              {level}
            </Text>
            <div style={{ flex: 1, height: compact ? 6 : 8, borderRadius: 4, background: "#f0f0f0", overflow: "hidden" }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  borderRadius: 4,
                  background: colors.bar,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            <Text style={{ width: 40, textAlign: "right", fontSize: 11, color: "#8c8c8c", fontFamily: "monospace" }}>
              {typeof score === "number" ? score.toFixed(2) : "—"}
            </Text>
          </div>
        );
      })}
    </div>
  );
};

// ── Confidence meter ──
const ConfidenceMeter = ({ value }) => {
  if (value === null || value === undefined) return <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>;

  const pct = Math.round(value * 100);
  const color = pct >= 75 ? "#52c41a" : pct >= 50 ? "#faad14" : "#f5222d";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, maxWidth: 120, height: 6, borderRadius: 3, background: "#f0f0f0", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: color, transition: "width 0.4s ease" }} />
      </div>
      <Text style={{ fontSize: 12, fontWeight: 600, color, fontFamily: "monospace" }}>{pct}%</Text>
    </div>
  );
};

// ── Section wrapper ──
const SectionCard = ({ icon, title, subtitle, children, accentColor = "#1890ff" }) => (
  <div
    style={{
      marginBottom: 16,
      borderRadius: 10,
      border: "1px solid #f0f0f0",
      overflow: "hidden",
      background: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    <div
      style={{
        padding: "14px 20px",
        borderBottom: "1px solid #f5f5f5",
        background: "linear-gradient(135deg, #fafafa 0%, #fff 100%)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: `${accentColor}10`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accentColor,
          fontSize: 16,
        }}
      >
        {icon}
      </div>
      <div>
        <Text strong style={{ fontSize: 14, color: "#111827", display: "block", lineHeight: "18px" }}>
          {title}
        </Text>
        <Text type="secondary" style={{ fontSize: 11, lineHeight: "14px" }}>
          {subtitle}
        </Text>
      </div>
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

// ══════════════════════════════════════════════════════════════
// ██  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
const CourseLevelTab = ({ courseId }) => {
  const dispatch = useDispatch();
  const { courseInfo, courseFilters } = useSelector((state) => state.courses);

  const [levelAi, setLevelAi] = useState(null);
  const [saving, setSaving] = useState(false);

  const effectiveId = courseInfo?._id || courseId;
  const levelList = courseFilters?.level || [];

  // Existing level (populated object from course schema)
  const existingLevel = courseInfo?.level || null;
  const existingLevelName =
    existingLevel && typeof existingLevel === "object"
      ? existingLevel.name || existingLevel.code || null
      : typeof existingLevel === "string"
      ? existingLevel
      : null;

  // Heuristic level
  const heuristic = courseInfo?.level_heuristic || null;

  // Hydrate AI level from course on load
  useEffect(() => {
    if (courseInfo?.level_ai) {
      setLevelAi(courseInfo.level_ai);
    }
  }, [courseInfo]);

  // Level dropdown options from master list
  const levelOptions = useMemo(
    () =>
      VALID_LABELS.map((label) => {
        const match = levelList.find(
          (l) =>
            (l.name || "").toLowerCase() === label ||
            (l.code || "").toLowerCase() === label
        );
        return {
          value: label,
          label: label.charAt(0).toUpperCase() + label.slice(1),
          levelId: match?._id || null,
        };
      }),
    [levelList]
  );

  // Resolve label → level_id
  const resolveLevelId = (label) => {
    const opt = levelOptions.find((o) => o.value === label);
    return opt?.levelId || null;
  };

  // ── AI Generation Handler ──
  const handleAIGenerate = (payload) => {
    const normalized = normalizeLevelFromAI(payload);
    if (!normalized) {
      message.error("AI did not return a valid level classification.");
      return;
    }
    setLevelAi(normalized);
    message.info("Level classification generated. Review and save.");
  };

  // ── Save Handler ──
  const handleSave = async () => {
    if (!effectiveId || !levelAi?.label) {
      message.warning("No level to save. Generate or select a level first.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: levelAi.label,
        level_id: resolveLevelId(levelAi.label),
        confidence: levelAi.confidence ?? null,
        scores: levelAi.scores || null,
        reasoning: levelAi.reasoning || null,
        signals: levelAi.signals || null,
        source: "ai",
        agreement_with_heuristic: heuristic
          ? levelAi.label === heuristic.label
          : null,
        agreement_with_existing: existingLevelName
          ? levelAi.label === existingLevelName.toLowerCase()
          : null,
        updated_at: new Date().toISOString(),
      };
      await dispatch(saveCourseLevel(effectiveId, payload));
      message.success("AI Level saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      message.error("Failed to save AI Level.");
    } finally {
      setSaving(false);
    }
  };

  // ── Agreement indicators ──
  const renderAgreementBadges = () => {
    if (!levelAi?.label) return null;

    const badges = [];

    if (heuristic?.label) {
      const agrees = levelAi.label === heuristic.label;
      badges.push(
        <Tag
          key="heuristic"
          icon={agrees ? <CheckCircleOutlined /> : <WarningOutlined />}
          color={agrees ? "success" : "warning"}
          style={{ fontSize: 11 }}
        >
          {agrees ? "Agrees with heuristic" : `Disagrees with heuristic (${heuristic.label})`}
        </Tag>
      );
    }

    if (existingLevelName) {
      const agrees = levelAi.label === existingLevelName.toLowerCase();
      badges.push(
        <Tag
          key="existing"
          icon={agrees ? <CheckCircleOutlined /> : <WarningOutlined />}
          color={agrees ? "success" : "warning"}
          style={{ fontSize: 11 }}
        >
          {agrees ? "Agrees with existing" : `Disagrees with existing (${existingLevelName})`}
        </Tag>
      );
    }

    if (badges.length === 0) return null;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
        {badges}
      </div>
    );
  };

  const aiLabelColors = levelAi?.label ? LEVEL_COLORS[levelAi.label] : null;

  return (
    <div className="card">
      <div className="card-body">
        <h4
          className="card-title mb-4"
          style={{ fontWeight: 600, fontSize: "16px", color: "#111827" }}
        >
          Course Level Analysis
        </h4>

        {/* ═══ Section 1: Existing Level ═══ */}
        <SectionCard
          icon={<InfoCircleOutlined />}
          title="Existing Level"
          subtitle="Current level from course schema"
          accentColor="#1890ff"
        >
          {existingLevelName ? (
            <Tag
              color={LEVEL_COLORS[existingLevelName.toLowerCase()]?.tag || "default"}
              style={{ fontSize: 14, padding: "4px 16px", borderRadius: 6 }}
            >
              {existingLevelName}
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontStyle: "italic" }}>
              No level assigned to this course.
            </Text>
          )}
        </SectionCard>

        {/* ═══ Section 2: Heuristic Level ═══ */}
        <SectionCard
          icon={<ExperimentOutlined />}
          title="Heuristic Level"
          subtitle="Computed by rule-based classification script"
          accentColor="#faad14"
        >
          {heuristic ? (
            <div>
              {/* Top row: label + confidence + source */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Label</Text>
                  <Tag
                    color={LEVEL_COLORS[heuristic.label]?.tag || "default"}
                    style={{ fontSize: 13, padding: "2px 12px", borderRadius: 5 }}
                  >
                    {heuristic.label}
                  </Tag>
                </div>
                <div style={{ minWidth: 150 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Confidence</Text>
                  <ConfidenceMeter value={heuristic.confidence} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Source</Text>
                  <Tag style={{ fontSize: 11 }}>{heuristic.source || "rule"}</Tag>
                </div>
                {heuristic.updated_at && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Updated</Text>
                    <Tooltip title={moment(heuristic.updated_at).format("LLLL")}>
                      <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {moment(heuristic.updated_at).fromNow()}
                      </Text>
                    </Tooltip>
                  </div>
                )}
              </div>

              {/* Scores breakdown */}
              {heuristic.scores && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                    Score Breakdown
                  </Text>
                  <ScoresBar scores={heuristic.scores} />
                </div>
              )}
            </div>
          ) : (
            <Text type="secondary" style={{ fontStyle: "italic" }}>
              Heuristic level has not been computed yet for this course.
            </Text>
          )}
        </SectionCard>

        {/* ═══ Section 3: AI Level ═══ */}
        <SectionCard
          icon={<RobotOutlined />}
          title="AI Level"
          subtitle="AI-generated classification — editable before saving"
          accentColor="#722ed1"
        >
          {/* AI Generation bar */}
          <div
            style={{
              background: "#f8f9fa",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              marginBottom: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              {effectiveId && (
                <AIGenerateAction
                  onGenerateSuccess={handleAIGenerate}
                  category="Level"
                  mode="json"
                  showText={true}
                  buttonText="Classify Level"
                  courseId={effectiveId}
                  defaultContextFields={["title", "headlines", "description", "out_come", "skills"]}
                  excludeContextFields={["level"]}
                  systemPromptDefault={
                    "You are an expert course difficulty classifier. Analyze course metadata — title, description, learning outcomes, and skills taught — to determine the appropriate difficulty level.\n\n" +
                    "Classification Framework:\n" +
                    "• BEGINNER: Introductory content. No prerequisites assumed. Uses simple, non-technical language. Targets complete newcomers. Covers foundational concepts.\n" +
                    "• INTERMEDIATE: Builds on foundational knowledge. Assumes basic familiarity with the subject. Introduces moderate complexity. Targets learners with some experience.\n" +
                    "• ADVANCED: Requires significant prior knowledge. Covers specialized, complex, or expert-level topics. Uses advanced terminology. Targets experienced practitioners.\n\n" +
                    "Key Signals to Evaluate:\n" +
                    "1. Prerequisites — explicit or implied requirements\n" +
                    "2. Vocabulary complexity — basic vs. technical/specialized terminology\n" +
                    "3. Learning outcomes — fundamental skills vs. advanced competencies\n" +
                    "4. Target audience — beginners, professionals, experts\n" +
                    "5. Skills covered — foundational vs. niche/specialized skills\n" +
                    "6. Content depth — survey-level vs. deep-dive"
                  }
                  contextInstructions={
                    'Classify this course\'s difficulty level. Output STRICTLY a JSON object with these exact keys:\n' +
                    '- "label": exactly one of "beginner", "intermediate", or "advanced" (lowercase)\n' +
                    '- "confidence": float between 0.0 and 1.0 representing your classification confidence\n' +
                    '- "scores": { "beginner": float, "intermediate": float, "advanced": float } — probability distribution summing approximately to 1.0\n' +
                    '- "reasoning": 1-2 sentence explanation of your classification rationale\n' +
                    '- "signals": { "prerequisites_detected": boolean, "technical_depth": "basic"|"moderate"|"deep", "target_audience": string (brief, max 10 words) }'
                  }
                />
              )}
            </div>
          </div>

          {/* AI Level Result / Editor */}
          {levelAi ? (
            <div>
              {/* Label selector + confidence */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 20, marginBottom: 14 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    Classified Level
                  </Text>
                  <Select
                    value={levelAi.label}
                    onChange={(val) => setLevelAi((prev) => ({ ...prev, label: val }))}
                    options={levelOptions}
                    style={{ width: 180 }}
                  />
                </div>
                {levelAi.confidence !== null && levelAi.confidence !== undefined && (
                  <div style={{ minWidth: 150 }}>
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                      AI Confidence
                    </Text>
                    <ConfidenceMeter value={levelAi.confidence} />
                  </div>
                )}
              </div>

              {/* Reasoning */}
              {levelAi.reasoning && (
                <div style={{ marginBottom: 14 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    Reasoning
                  </Text>
                  <div
                    style={{
                      padding: "10px 14px",
                      background: aiLabelColors ? `${aiLabelColors.bg}` : "#fafafa",
                      border: `1px solid ${aiLabelColors ? aiLabelColors.border : "#f0f0f0"}`,
                      borderRadius: 6,
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: "20px",
                    }}
                  >
                    {levelAi.reasoning}
                  </div>
                </div>
              )}

              {/* Scores */}
              {levelAi.scores && (
                <div style={{ marginBottom: 14 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                    Probability Scores
                  </Text>
                  <ScoresBar scores={levelAi.scores} compact />
                </div>
              )}

              {/* Signals */}
              {levelAi.signals && (
                <div style={{ marginBottom: 14 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                    Detected Signals
                  </Text>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {levelAi.signals.prerequisites_detected !== undefined && (
                      <Tag
                        color={levelAi.signals.prerequisites_detected ? "orange" : "green"}
                        style={{ fontSize: 11 }}
                      >
                        Prerequisites: {levelAi.signals.prerequisites_detected ? "Yes" : "No"}
                      </Tag>
                    )}
                    {levelAi.signals.technical_depth && (
                      <Tag
                        style={{
                          fontSize: 11,
                          color: DEPTH_COLORS[levelAi.signals.technical_depth] || "#8c8c8c",
                          borderColor: DEPTH_COLORS[levelAi.signals.technical_depth] || "#d9d9d9",
                        }}
                      >
                        Depth: {levelAi.signals.technical_depth}
                      </Tag>
                    )}
                    {levelAi.signals.target_audience && (
                      <Tag style={{ fontSize: 11 }}>
                        Audience: {levelAi.signals.target_audience}
                      </Tag>
                    )}
                  </div>
                </div>
              )}

              {/* Agreement badges */}
              {renderAgreementBadges()}
            </div>
          ) : (
            <Text type="secondary" style={{ fontStyle: "italic" }}>
              No AI level generated yet. Click "Classify Level" above or manually select a level after generation.
            </Text>
          )}

          {/* Save button */}
          <div
            className="d-flex justify-content-end mt-4 pt-3"
            style={{ borderTop: "1px solid #f0f0f0" }}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!levelAi?.label}
              style={{
                minWidth: "140px",
                height: "36px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(114, 46, 209, 0.15)",
                background: "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
                border: "none",
              }}
            >
              Save AI Level
            </Button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default CourseLevelTab;
